var _class, _descriptor, _descriptor2;
function _initializerDefineProperty(target, property, descriptor, context) { if (!descriptor) return; Object.defineProperty(target, property, { enumerable: descriptor.enumerable, configurable: descriptor.configurable, writable: descriptor.writable, value: descriptor.initializer ? descriptor.initializer.call(context) : void 0 }); }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : String(i); }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) { var desc = {}; Object.keys(descriptor).forEach(function (key) { desc[key] = descriptor[key]; }); desc.enumerable = !!desc.enumerable; desc.configurable = !!desc.configurable; if ('value' in desc || desc.initializer) { desc.writable = true; } desc = decorators.slice().reverse().reduce(function (desc, decorator) { return decorator(target, property, desc) || desc; }, desc); if (context && desc.initializer !== void 0) { desc.value = desc.initializer ? desc.initializer.call(context) : void 0; desc.initializer = undefined; } if (desc.initializer === void 0) { Object.defineProperty(target, property, desc); desc = null; } return desc; }
function _initializerWarningHelper(descriptor, context) { throw new Error('Decorating class property failed. Please ensure that ' + 'transform-class-properties is enabled and runs after the decorators transform.'); }
import { GraphQLClient } from '@ember-graphql-client/client/services/graphql';
import { GraphQLRequestClient } from '@ember-graphql-client/client/utils/graphql-request-client';
import { assert } from '@ember/debug';
import { assign } from '@ember/polyfills';
import Service, { inject as service } from '@ember/service';
import { compare } from '@ember/utils';
import availableEventNamesQuery from '@fabscale/ember-fabscale-analytics/gql/queries/available-event-names.graphql';
import availablePropertyNamesQuery from '@fabscale/ember-fabscale-analytics/gql/queries/available-property-names.graphql';
import eventIdsQuery from '@fabscale/ember-fabscale-analytics/gql/queries/event-ids.graphql';
import eventsQuery from '@fabscale/ember-fabscale-analytics/gql/queries/events.graphql';
import numericActionGroupedQuery from '@fabscale/ember-fabscale-analytics/gql/queries/numeric-action-grouped.graphql';
import numericActionIntervalQuery from '@fabscale/ember-fabscale-analytics/gql/queries/numeric-action-interval.graphql';
import numericActionQuery from '@fabscale/ember-fabscale-analytics/gql/queries/numeric-action.graphql';
import numericPropertyActionQuery from '@fabscale/ember-fabscale-analytics/gql/queries/numeric-property-action.graphql';
import stringPropertyActionQuery from '@fabscale/ember-fabscale-analytics/gql/queries/string-property-action.graphql';
import { validateDate } from '@fabscale/ember-fabscale-analytics/utils/validate-date';
import { AnalyticsReadNumericActionType, AnalyticsReadNumericPropertyActionType, AnalyticsReadStringPropertyActionType } from '..';
import fetch from 'fetch';
let AnalyticsReadService = (_class = class AnalyticsReadService extends Service {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "graphql", _descriptor, this);
    _initializerDefineProperty(this, "analyticsConfig", _descriptor2, this);
    _defineProperty(this, "_client", void 0);
  }
  getGraphQLClient() {
    if (this._client) {
      return this._client;
    }
    let {
      apiUrl,
      readKey
    } = this.analyticsConfig;
    let headers = readKey ? {
      Authorization: readKey
    } : {};
    let internalClient = new GraphQLClient(apiUrl, {
      headers,
      fetch
    });
    let client = new GraphQLRequestClient(internalClient);
    this._client = client;
    return client;
  }
  async getEvents(eventArgs) {
    let variables = getEventVariables(eventArgs);
    return this._getEvents(variables);
  }
  async getEventIds(eventArgs) {
    let variables = getEventVariables(eventArgs);
    return this.graphql.query({
      query: eventIdsQuery,
      variables,
      namespace: 'eventIds'
    }, {}, this.getGraphQLClient());
  }
  async getAvailableEventNames({
    dateFrom,
    dateTo
  }) {
    validateDate(dateFrom);
    validateDate(dateTo);
    let variables = {
      dateFrom,
      dateTo
    };
    return this.graphql.query({
      query: availableEventNamesQuery,
      variables,
      namespace: 'availableEventNames'
    }, {}, this.getGraphQLClient());
  }
  async getAvailablePropertyNames({
    dateFrom,
    dateTo,
    name
  }) {
    validateDate(dateFrom);
    validateDate(dateTo);
    assert('getAvailablePropertyNames: name is required', !!name);
    let variables = {
      dateFrom,
      dateTo,
      name
    };
    let {
      stringProperties,
      numericProperties
    } = await this.graphql.query({
      query: availablePropertyNamesQuery,
      variables,
      namespace: 'availablePropertyNames'
    }, {}, this.getGraphQLClient());
    return {
      stringProperties,
      numericProperties
    };
  }

  // COUNT
  async count(eventArgs) {
    let variables = Object.assign({
      action: AnalyticsReadNumericActionType.Count
    }, getEventVariables(eventArgs));
    return this.graphql.query({
      query: numericActionQuery,
      variables,
      namespace: 'numericAction'
    }, {}, this.getGraphQLClient());
  }
  async countGrouped(eventArgs, groupByPropertyName, groupByPropertyIsNumeric = false) {
    let variables = Object.assign({
      action: AnalyticsReadNumericActionType.Count,
      groupByPropertyName,
      groupByPropertyIsNumeric
    }, getEventVariables(eventArgs));
    return this.graphql.query({
      query: numericActionGroupedQuery,
      variables,
      namespace: 'numericActionGrouped'
    }, {}, this.getGraphQLClient());
  }
  async countInterval(eventArgs) {
    let variables = Object.assign({
      action: AnalyticsReadNumericActionType.Count
    }, getEventVariables(eventArgs));
    return this.graphql.query({
      query: numericActionIntervalQuery,
      variables,
      namespace: 'numericActionInterval'
    }, {}, this.getGraphQLClient());
  }

  // Average
  async averageProperty(eventArgs, propertyName) {
    let variables = Object.assign({
      action: AnalyticsReadNumericPropertyActionType.Average,
      propertyName
    }, getEventVariables(eventArgs));
    return this.graphql.query({
      query: numericPropertyActionQuery,
      variables,
      namespace: 'numericPropertyAction'
    }, {}, this.getGraphQLClient());
  }

  // Sum
  async sumProperty(eventArgs, propertyName) {
    let variables = Object.assign({
      action: AnalyticsReadNumericPropertyActionType.Sum,
      propertyName
    }, getEventVariables(eventArgs));
    return this.graphql.query({
      query: numericPropertyActionQuery,
      variables,
      namespace: 'numericPropertyAction'
    }, {}, this.getGraphQLClient());
  }

  // Unique
  async uniqueProperties(eventArgs, propertyName) {
    let variables = Object.assign({
      action: AnalyticsReadStringPropertyActionType.Unique,
      propertyName
    }, getEventVariables(eventArgs));
    return this.graphql.query({
      query: stringPropertyActionQuery,
      variables,
      namespace: 'stringPropertyAction'
    }, {}, this.getGraphQLClient());
  }

  // Private
  async _getEvents(variables) {
    let events = await this.graphql.query({
      query: eventsQuery,
      variables,
      namespace: 'events'
    }, {}, this.getGraphQLClient());
    return events.map(event => {
      let {
        stringProperties,
        numericProperties
      } = event;
      let properties = stringProperties.concat(numericProperties);
      event.properties = properties = properties.sort((a, b) => compare(a.name, b.name));
      return assign({}, event, {
        properties
      });
    });
  }
}, (_descriptor = _applyDecoratedDescriptor(_class.prototype, "graphql", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "analyticsConfig", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
})), _class);
export { AnalyticsReadService as default };
function getEventVariables({
  dateFrom,
  dateTo,
  name,
  eventIds,
  stringProperties = [],
  numericProperties = [],
  properties
}) {
  validateDate(dateFrom);
  validateDate(dateTo);
  assert('getEventVariables: name must be set', !!name);
  let actualStringProperties = stringProperties || [];
  let actualNumericProperties = numericProperties || [];

  // This is a short-cut to make filtering easier
  // It will detect if a filter is numeric or a string automatically
  // If you want to only filter for existence of a property (so without value)
  // you _have_ to use the dedicated stringProperties/numericProperties options
  if (properties && properties.length) {
    properties.forEach(property => {
      if (isNumericProperty(property)) {
        actualNumericProperties.push(property);
      }
      if (isStringProperty(property)) {
        actualStringProperties.push(property);
      }
    });
  }

  // Ensure empty values are deleted
  stringProperties = stringProperties.filter(stringProperty => stringProperty.name).map(stringProperty => {
    if (typeof stringProperty.value !== 'string') {
      delete stringProperty.value;
    }
    return stringProperty;
  });
  numericProperties = numericProperties.filter(numericProperty => numericProperty.name).map(numericProperty => {
    if (typeof numericProperty.value !== 'number') {
      delete numericProperty.value;
    }
    return numericProperty;
  });
  return {
    name,
    dateFrom,
    dateTo,
    eventIds,
    filterStringProperties: stringProperties,
    filterNumericProperties: numericProperties
  };
}
function isStringProperty(property) {
  return typeof property.value === 'string';
}
function isNumericProperty(property) {
  return typeof property.value === 'number';
}